import { OpenFaireDirectFirstOrderIncentiveModalButton } from "@faire/deprecated-retailer-visitor-shared/components/Modals/FaireDirectModals/FaireDirectFirstOrderIncentiveModal/OpenFaireDirectFirstOrderIncentiveModalButton";
import { useFaireDirectFirstOrderIncentive } from "@faire/deprecated-retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import {
  useAssignSettingEnableFDRASitewideBannerUpdates,
  useSettingEnableFDRASitewideBannerUpdates,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableFDRASitewideBannerUpdates";
import { trackFaireDirectFaireDirectFoiTopNavBannerCtaActionCategoryClick } from "@faire/web-api--source/events/faireDirect/actionCategoryClick/faireDirectFoiTopNavBannerCta";
import brandPage from "@faire/web-api--source/routes/www/brand/brandToken";
import {
  InlineLinkFormatting,
  MediumFormatting,
} from "@faire/web/common/localization/formatting";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect, type JSX } from "react";

enum LinkAction {
  OpenModal = "OpenModal",
  RedirectToBrandPage = "RedirectToBrandPage",
}

export const FaireDirectFirstOrderIncentiveBanner: React.FC = () => {
  const {
    brandName,
    isLastDayBeforeExpiry,
    isSecondLastDayBeforeExpiry,
    isFaireDirectCreditPercentageBased,
    formattedFixedBasedAmount,
    formattedPercentageBasedNumber,
    remainingDaysBeforeExpiryInTreatmentGroup,
    brandToken,
  } = useFaireDirectFirstOrderIncentive();

  if (!brandName || !brandToken) {
    return null;
  }

  return isFaireDirectCreditPercentageBased ? (
    <PercentageBasedBanner
      isLastDayBeforeExpiry={isLastDayBeforeExpiry}
      isSecondLastDayBeforeExpiry={isSecondLastDayBeforeExpiry}
      formattedPercentageBasedNumber={formattedPercentageBasedNumber}
      brandName={brandName}
      brandToken={brandToken}
      remainingDaysBeforeExpiryInTreatmentGroup={
        remainingDaysBeforeExpiryInTreatmentGroup
      }
    />
  ) : (
    <AmoutBasedBanner
      isLastDayBeforeExpiry={isLastDayBeforeExpiry}
      isSecondLastDayBeforeExpiry={isSecondLastDayBeforeExpiry}
      formattedFixedBasedAmount={formattedFixedBasedAmount}
      brandName={brandName}
    />
  );
};

const BannerWrapper = ({ children }: { children: React.ReactNode }) => (
  <Typography
    align="center"
    data-test-id="faire-direct-first-order-incentive-banner"
  >
    {children}
  </Typography>
);

const trackCtaClick = (linkAction: LinkAction) => {
  trackFaireDirectFaireDirectFoiTopNavBannerCtaActionCategoryClick(linkAction);
};

const PercentageBasedBanner: React.FC<{
  formattedPercentageBasedNumber: string;
  brandName: string;
  brandToken: string;
  isLastDayBeforeExpiry: boolean;
  isSecondLastDayBeforeExpiry: boolean;
  remainingDaysBeforeExpiryInTreatmentGroup: number;
}> = ({
  remainingDaysBeforeExpiryInTreatmentGroup,
  formattedPercentageBasedNumber,
  brandName,
  brandToken,
  isLastDayBeforeExpiry,
  isSecondLastDayBeforeExpiry,
}) => {
  const assignSettingEnableFDRASitewideBannerUpdates =
    useAssignSettingEnableFDRASitewideBannerUpdates();
  const enableFDRASitewideBannerUpdates =
    useSettingEnableFDRASitewideBannerUpdates();

  useEffect(() => {
    assignSettingEnableFDRASitewideBannerUpdates();
  }, [assignSettingEnableFDRASitewideBannerUpdates]);

  if (enableFDRASitewideBannerUpdates) {
    return (
      <PercentageBasedBannerInTreatmentGroup
        remainingDaysBeforeExpiry={remainingDaysBeforeExpiryInTreatmentGroup}
        formattedPercentageBasedNumber={formattedPercentageBasedNumber}
        brandName={brandName}
        brandToken={brandToken}
      />
    );
  }
  return (
    <PercentageBasedBannerInControlGroup
      isLastDayBeforeExpiry={isLastDayBeforeExpiry}
      isSecondLastDayBeforeExpiry={isSecondLastDayBeforeExpiry}
      formattedPercentageBasedNumber={formattedPercentageBasedNumber}
      brandName={brandName}
    />
  );
};

const PercentageBasedBannerInTreatmentGroup: React.FC<{
  remainingDaysBeforeExpiry: number;
  formattedPercentageBasedNumber: string;
  brandName: string;
  brandToken: string;
}> = ({
  remainingDaysBeforeExpiry,
  formattedPercentageBasedNumber,
  brandName,
  brandToken,
}) => {
  if (remainingDaysBeforeExpiry === 0) {
    return null;
  }

  const BrandPageOpenLinkFormatting = InlineLinkFormatting({
    linkTo: brandPage(brandToken),
    onClick: () => trackCtaClick(LinkAction.RedirectToBrandPage),
  });

  if (remainingDaysBeforeExpiry === 1) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="OsYwAA"
          defaultMessage="<MediumFormatting>Expires today!</MediumFormatting> {percentageBasedNumber} off & free shipping with {brandCompanyName}. <BrandPageOpenLinkFormatting>Start buying</BrandPageOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is less than 24 hours (a day).",
            img: "https://cdn.faire.com/fastly/bd0e0b79b6f12003cf4c13076086205ec972ac650a078b478b861d567f05261a.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            BrandPageOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else if (remainingDaysBeforeExpiry <= 3) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="kSuaiY"
          defaultMessage="<MediumFormatting>{daysLeftBeforeExpiryNumber} days left!</MediumFormatting> {percentageBasedNumber} off & free shipping with {brandCompanyName}. <BrandPageOpenLinkFormatting>Start buying</BrandPageOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when there is 2 or 3 days left before the incentive expires.",
            img: "https://cdn.faire.com/fastly/17b3de3919bc41a8ad341c0d47f3053d611f0e1f9371f9f8cb090776941903e9.png",
          }}
          values={{
            daysLeftBeforeExpiryNumber: remainingDaysBeforeExpiry,
            MediumFormatting: MediumFormatting,
            BrandPageOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="R2dpKt"
          defaultMessage="{percentageBasedNumber} off & free shipping with {brandCompanyName}. Expires in {daysLeftBeforeExpiryNumber} days. <BrandPageOpenLinkFormatting>Start buying</BrandPageOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is greater than 3 days.",
            img: "https://cdn.faire.com/fastly/bb00b6598c3769b1ea0bc7435495a92638ff02cd004bf6877389aa1adbf09e98.png",
          }}
          values={{
            daysLeftBeforeExpiryNumber: remainingDaysBeforeExpiry,
            BrandPageOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  }
};

const PercentageBasedBannerInControlGroup: React.FC<{
  isLastDayBeforeExpiry: boolean;
  isSecondLastDayBeforeExpiry: boolean;
  formattedPercentageBasedNumber: string;
  brandName: string;
}> = ({
  isLastDayBeforeExpiry,
  isSecondLastDayBeforeExpiry,
  formattedPercentageBasedNumber,
  brandName,
}) => {
  const IncentiveModalOpenLinkFormatting = (chunks: JSX.Element) =>
    OpenFaireDirectFirstOrderIncentiveModalButton(chunks, () =>
      trackCtaClick(LinkAction.OpenModal)
    );

  if (isLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="nY9MvF"
          defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is less than 24 hours (a day).",
            img: "https://cdn.faire.com/fastly/a33af7ae726a495f055999255118d5c238d000ef597ac303d2e3af485d6d2ae4.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else if (isSecondLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="h4xXFU"
          defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is less than 2 days.",
            img: "https://cdn.faire.com/fastly/cf58be7e89b13a0be4c1e6a313f0d5edd73f94a0f9cf18a21bb685b34aa16da1.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="kTZptx"
          defaultMessage="Get {percentageBasedNumber} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct percent based discount. This only show up when the remaining time is greater than 2 days.",
            img: "https://cdn.faire.com/fastly/f392415027ca1ebb6d7798cc86cc5cb72fe04ac828c1d50fe62ed8d2e1ceca30.png",
          }}
          values={{
            IncentiveModalOpenLinkFormatting,
            percentageBasedNumber: formattedPercentageBasedNumber,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  }
};

const AmoutBasedBanner: React.FC<{
  isLastDayBeforeExpiry: boolean;
  isSecondLastDayBeforeExpiry: boolean;
  formattedFixedBasedAmount: string;
  brandName: string;
}> = ({
  isLastDayBeforeExpiry,
  isSecondLastDayBeforeExpiry,
  formattedFixedBasedAmount,
  brandName,
}) => {
  if (isLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="ec4bOv"
          defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is less than 24 hours (a day).",
            img: "https://cdn.faire.com/fastly/a33af7ae726a495f055999255118d5c238d000ef597ac303d2e3af485d6d2ae4.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else if (isSecondLastDayBeforeExpiry) {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="M14xGb"
          defaultMessage="<MediumFormatting>2 days left!</MediumFormatting> Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is less than 2 days.",
            img: "https://cdn.faire.com/fastly/cf58be7e89b13a0be4c1e6a313f0d5edd73f94a0f9cf18a21bb685b34aa16da1.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  } else {
    return (
      <BannerWrapper>
        <StrictLocalMsg
          id="sceXEx"
          defaultMessage="Get {promoMoney} off & free shipping with {brandCompanyName}. <IncentiveModalOpenLinkFormatting>View details</IncentiveModalOpenLinkFormatting>"
          description={{
            text: "Banner at the top of the page that promotes a Faire Direct dollar based discount. This only show up when the remaining time is greater than 2 days.",
            img: "https://cdn.faire.com/fastly/f392415027ca1ebb6d7798cc86cc5cb72fe04ac828c1d50fe62ed8d2e1ceca30.png",
          }}
          values={{
            IncentiveModalOpenLinkFormatting:
              OpenFaireDirectFirstOrderIncentiveModalButton,
            promoMoney: formattedFixedBasedAmount,
            brandCompanyName: brandName,
          }}
        />
      </BannerWrapper>
    );
  }
};
